import {
  getCheckQuickBit,
  getCheckNeteller,
  getCheckSkrill,
  getStoredEcheck,
  getCreditCards,
  getRules,
  getPaymentRules,
  getAllCryptoWallets,
  getCheckRestricted,
  createCryptoWallet,
  searchBlockElements,
  getAvailableDepositMethods,
  getFailedTransactionResult,
  getSuccessTransactionResult,
  createDepositForRegisteredCustomer,
  getGlobalLimits,
  resetGlobalLimits,
  changeGlobalLimits,
  createPDF,
} from '@/utils/api';
import Vue from 'vue';
import Vuex from 'vuex';
import { getField, updateField } from 'vuex-map-fields';
import moment from 'moment';
import groupBy from 'lodash/groupBy';
import { debug } from 'util';

Vue.use(Vuex);

const state = {
  selectedDepositMethod: 'MONEYGRAM', // 'CC' | 'EXISTING_CC' | 'E-CHECK' | 'WIRE' | 'BTC' | 'BCH'| 'LTC' | 'MONEYGRAM'
  amount: 100,
  filteredActiveCreditCards: [],
  filteredEcheck: [],
  restrictions: [],
  currentDepositMethod: '',
  cryptoWallets: [],
  postDepositLoading: false,
  rules: null,
  paymentRules: [],
  paymentRulesLoading: true,
  blockElements: [],
  quickbitCheck: false,
  netellerCheck: false,
  skrillCheck: false,
  skrillType: '',
  availableMethods: [],
  walletsStatus: {
    loading: false,
    success: false,
    error: false,
  },
  depositResultModel: {},
  createWalletStatus: {
    loading: false,
    success: false,
    error: false,
  },
  globalLimits: null,
  limitUpdateLoading: false,
  limitResetLoading: false,
};

const getters = {
  getField,
  getBillingAddress: state => {
    if (!state.selectedCreditCard) return '';
    return `
        ${state.selectedCreditCard.address}
        ${state.selectedCreditCard.country ? state.selectedCreditCard.country + ',' : ''}
        ${state.selectedCreditCard.state ? state.selectedCreditCard.state + ',' : ''}
        ${state.selectedCreditCard.city ? state.selectedCreditCard.city + ',' : ''}
        ${state.selectedCreditCard.zipCode}
        ${state.selectedCreditCard.phoneNumber}`;
  },
  filteredRestrictions: state => {
    var d = new Date();
    var myTimezone = 'America/New_York';
    var myDatetimeFormat = 'hh:mm:ss';
    var current = moment(d)
      .tz(myTimezone)
      .format(myDatetimeFormat);
    const result = {};
    state.restrictions.map(raw => {
      if (current < raw.final_hour && current > raw.initial_hour) {
        result[raw.payment_type] = raw;
      }
    });
    return result;
  },
};

const mutations = {
  updateField,
  setPostDepositLoading(state, loading) {
    state.postDepositLoading = loading;
  },
  setDepositMethod(state, method) {
    state.selectedDepositMethod = method;
  },
  setFilteredActiveCreditCards(state, arr) {
    state.filteredActiveCreditCards = arr;
  },
  setRules(state, obj) {
    state.rules = obj;
  },
  setPaymentRules(state, obj) {
    state.paymentRules = obj;
  },
  setPaymentRulesLoading(state, obj) {
    state.paymentRulesLoading = obj;
  },
  setCryptoWallets(state, arr) {
    state.cryptoWallets = arr;
  },
  setCurrentDepositMethod(state, method) {
    state.currentDepositMethod = method;
  },
  setDepositResultModel(state, arr) {
    state.depositResultModel = arr;
  },
  setWalletsStatus(state, row) {
    state.walletsStatus = {
      ...state.walletsStatus,
      ...row,
    };
  },
  setCreateCryptoWalletStatus(state, row) {
    state.createWalletStatus = {
      ...state.createWalletStatus,
      ...row,
    };
  },
  setBlockElements(state, arr) {
    state.blockElements = arr;
  },
  setAvailableMethods(state, arr) {
    state.availableMethods = arr;
  },
  setFilteredEcheck(state, arr) {
    state.filteredEcheck = arr;
  },
  setRestrictions(state, arr) {
    state.restrictions = arr;
  },
  setQuickbitCheck(state, check) {
    state.quickbitCheck = check;
  },
  setNetellerCheck(state, check) {
    state.netellerCheck = check;
  },
  setSkrillCheck(state, check) {
    state.skrillCheck = check;
  },
  setSkrillType(state, check) {
    state.skrillType = check;
  },
  setGlobalLimits(state, obj) {
    state.globalLimits = obj;
  },
  setLimitUpdateLoading(state, loading) {
    state.limitUpdateLoading = loading;
  },
  setLimitResetLoading(state, loading) {
    state.limitResetLoading = loading;
  },
};

const actions = {
  tryGetGlobalLimits({ commit }) {
    getGlobalLimits().then(r => {
      if (r.data.IsSuccess) {
        commit('setGlobalLimits', r.data.Data);
      }
    });
  },
  tryResetGlobalLimit({ commit, dispatch }) {
    commit('setLimitResetLoading', true);
    return new Promise((reject, resolve) => {
      resetGlobalLimits()
        .then(r => {
          if (r.data.IsSuccess && r.data.Data && r.data.Data.success) {
            dispatch('tryGetGlobalLimits');
          }
          resolve(r);
        })
        .catch(err => {
          reject(err);
        })
        .finally(() => {
          commit('setLimitResetLoading', false);
        });
    });
  },
  tryUpdateGlobalLimit({ dispatch, commit, state }, payload) {
    commit('setLimitUpdateLoading', true);
    var params = [];
    if (payload.da) {
      params.push('volumeDayRequested=' + payload.da);
    }
    if (payload.we) {
      params.push('volumeWeeklyRequested=' + payload.we);
    }
    if (payload.mo) {
      params.push('volumeMonthlyRequested=' + payload.mo);
    }
    var paramm = '?' + params.join('&');
    return new Promise((reject, resolve) => {
      changeGlobalLimits(paramm)
        .then(r => {
          if (r.data.IsSuccess && r.data.Data && r.data.Data.success) {
            dispatch('tryGetGlobalLimits');
          }
          resolve(r);
        })
        .catch(err => {
          reject(err);
        })
        .finally(() => {
          commit('setLimitUpdateLoading', false);
        });
    });
  },
  async tryGetAvailableMethods({ commit }) {
    await getAvailableDepositMethods().then(r => {
      if (r.data.IsSuccess) {
        const availableMethods = r.data.Data;
        // r.data.Data.push("ACH")
        // r.data.Data.push("QuickBit")
        commit('setAvailableMethods', availableMethods);
        if (availableMethods.includes('QuickBit'))
          getCheckQuickBit().then(r => {
            if (r.data.IsSuccess) {
              commit('setQuickbitCheck', r.data.Data.allow);
            }
          });
        if (availableMethods.includes('Neteller'))
          getCheckNeteller().then(r => {
            if (r.data.IsSuccess) {
              commit('setNetellerCheck', r.data.Data.allow);
            }
          });
        if (availableMethods.includes('Skrill'))
          getCheckSkrill().then(r => {
            if (r.data.IsSuccess) {
              commit('setSkrillCheck', r.data.Data.allow);
              commit('setSkrillType', r.data.Data.paymentType);
            }
          });
      }
    });
  },
  tryGetCreditCards({ commit }) {
    getCreditCards().then(r => {
      if (r.data.IsSuccess) {
        const cardsData = r.data.Data;
        const cardsToShow = 4;
        const filteredActiveCreditCards = cardsData.activeCards.filter(
          card =>
            card.cardNumber &&
            card.cardBrand &&
            (card.cardBrand.toLowerCase() === 'visa' || card.cardBrand.toLowerCase() === 'mastercard') &&
            card.cardNumber.length >= 13,
        );
        // .slice(0, cardsToShow);

        commit('setFilteredActiveCreditCards', filteredActiveCreditCards);
      }
    });
  },
  tryGetRules({ commit }) {
    getRules().then(r => {
      if (r.data.IsSuccess) {
        commit('setRules', r.data.Data);
      }
    });
  },
  tryGetPaymentRules({ commit }) {
    commit('setPaymentRulesLoading', true);
    getPaymentRules().then(r => {
      if (r.data.IsSuccess) {
        const result = {};
        r.data.Data.map(raw => {
          result[raw.payment_type] = raw;
        });
        commit('setPaymentRules', result);
      }
      commit('setPaymentRulesLoading', false);
    });
  },
  tryGetRestrictions({ commit }) {
    getCheckRestricted().then(r => {
      if (r.data.IsSuccess) {
        // r.data.Data.restrictions = [
        //   {
        //     "id": 1,
        //     "brand_id": 1,
        //     "payment_type": "Crypto",
        //     "initial_hour": "01:00:00",
        //     "final_hour": "18:00:00",
        //     "min_amount": "250",
        //     "allow_internet": false,
        //     "allow_inhouse": true,
        //     "active": true,
        //     "created_at": "2019-09-17 18:55:45",
        //     "created_by": "jvega",
        //     "updated_at": "2019-11-03 20:27:35",
        //     "updated_by": "wchacon"
        //   }
        // ];
        commit('setRestrictions', r.data.Data.restrictions);
      }
    });
  },
  tryGetFailedTransactionResult({ commit }) {
    getFailedTransactionResult().then(r => {
      if (r.data.IsSuccess) {
        commit('setDepositResultModel', r.data.Data);
      } else {
        commit('setDepositResultModel', { success: false, message: 'default' });
      }
    });
  },
  tryGetSuccessTransactionResult({ commit }) {
    getSuccessTransactionResult().then(r => {
      if (r.data.IsSuccess) {
        commit('setDepositResultModel', r.data.Data);
      } else {
        commit('setDepositResultModel', { success: true, message: 'default' });
      }
    });
  },
  tryGetAllCryptoWallets({ commit }) {
    commit('setWalletsStatus', { loading: true, success: false, error: false });
    getAllCryptoWallets().then(r => {
      if (r.data.IsSuccess) {
        commit('setCryptoWallets', r.data.Data);
        commit('setWalletsStatus', { success: true, error: false });
      } else {
        commit('setWalletsStatus', { success: false, error: true });
      }
      commit('setWalletsStatus', { loading: false });
    });
  },
  tryCreateCryptoWallet({ dispatch, commit }, currencyType) {
    commit('setCreateCryptoWalletStatus', {
      loading: true,
      success: false,
      error: false,
    });
    createCryptoWallet(currencyType).then(r => {
      if (r.data.IsSuccess) {
        dispatch('tryGetAllCryptoWallets');
        commit('setCreateCryptoWalletStatus', { success: true, error: true });
      } else {
        commit('setCreateCryptoWalletStatus', { success: false, error: true });
      }
      commit('setCreateCryptoWalletStatus', { loading: false });
    });
  },
  tryCreateDepositForRegisteredCustomer({ dispatch, commit }, formData) {
    createDepositForRegisteredCustomer(formData);
  },
  tryCreatePDF({ dispatch, commit }, payload) {
    return createPDF(payload);
  },
  trySearchBlockElements({ commit }) {
    searchBlockElements({ type: 'deposit', blocks: 1 }).then(r => {
      if (r.data.IsSuccess) {
        commit('setBlockElements', r.data.Data);
      }
    });
  },
  tryGetStoredEcheck({ commit }) {
    getStoredEcheck().then(r => {
      if (r.data.IsSuccess) {
        // r.data.Data = [
        //   {
        //     "id": 30,
        //     "customerAccount": "MB4017",
        //     "check_holder": "ter",
        //     "name": "test",
        //     "last_name": "testsfsdf",
        //     "address": "123213 rue lachine",
        //     "city": "montreal",
        //     "state": "AL",
        //     "country": "US",
        //     "postal_code": "231232",
        //     "phone": "23423432",
        //     "email": "t@test.com",
        //     "bank_name": "royal",
        //     "bank_account_number": "5876543232543",
        //     "bank_routing_number": "062205704",
        //     "bank_account_type": "C",
        //     "bank_currency": "USD",
        //     "bank_trans_type": null,
        //     "bank_payment_type": null,
        //     "active": true,
        //     "createdBy": "gzhao",
        //     "updatedBy": null,
        //     "deletedBy": null,
        //     "created_at": "2018-12-31 14:04:23",
        //     "updated_at": "2018-12-31 14:04:23",
        //     "deleted_at": null
        //   },
        //   {
        //     "id": 23,
        //     "customerAccount": "MB4017",
        //     "check_holder": "Jose Perez",
        //     "name": "Jose",
        //     "last_name": "Perez Test",
        //     "address": "Paseo Colon, Torres Colon, Torre A, Piso 12",
        //     "city": "San Jose",
        //     "state": "AL",
        //     "country": "US",
        //     "postal_code": "10101",
        //     "phone": "62568741",
        //     "email": "jperez@gmail.com",
        //     "bank_name": "Bank Name",
        //     "bank_account_number": "5876543232543",
        //     "bank_routing_number": "062205704",
        //     "bank_account_type": "C",
        //     "bank_currency": "USD",
        //     "bank_trans_type": null,
        //     "bank_payment_type": null,
        //     "active": true,
        //     "createdBy": "wchacon",
        //     "updatedBy": null,
        //     "deletedBy": null,
        //     "created_at": "2018-12-31 13:44:18",
        //     "updated_at": "2018-12-31 13:44:18",
        //     "deleted_at": null
        //   },
        //   {
        //     "id": 16,
        //     "customerAccount": "MB4017",
        //     "check_holder": "atesfdsfsd",
        //     "name": "Gen",
        //     "last_name": "z",
        //     "address": "1234 rue lasalle",
        //     "city": "montreal",
        //     "state": "AL",
        //     "country": "US",
        //     "postal_code": "234567",
        //     "phone": "4123232323",
        //     "email": "gm-testing@email.com",
        //     "bank_name": "dsfsdfsdfdsf",
        //     "bank_account_number": "5876543232543",
        //     "bank_routing_number": "062205704",
        //     "bank_account_type": "C",
        //     "bank_currency": "USD",
        //     "bank_trans_type": null,
        //     "bank_payment_type": null,
        //     "active": true,
        //     "createdBy": "gzhao",
        //     "updatedBy": null,
        //     "deletedBy": null,
        //     "created_at": "2018-12-31 13:00:54",
        //     "updated_at": "2018-12-31 13:00:54",
        //     "deleted_at": null
        //   },
        //   {
        //     "id": 7,
        //     "customerAccount": "MB4017",
        //     "check_holder": "William",
        //     "name": "Testing",
        //     "last_name": "test",
        //     "address": "4735  Bryan Avenue",
        //     "city": "Montgomery",
        //     "state": "AL",
        //     "country": "US",
        //     "postal_code": "36104",
        //     "phone": "3343200913",
        //     "email": "c7t1pkufo7@thrubay.com",
        //     "bank_name": "test",
        //     "bank_account_number": "5876543232543",
        //     "bank_routing_number": "062205704",
        //     "bank_account_type": "C",
        //     "bank_currency": "USD",
        //     "bank_trans_type": null,
        //     "bank_payment_type": null,
        //     "active": true,
        //     "createdBy": "ernestobaezf",
        //     "updatedBy": "gzhao",
        //     "deletedBy": null,
        //     "created_at": "2018-12-27 15:41:47",
        //     "updated_at": "2019-01-03 15:49:13",
        //     "deleted_at": null
        //   }
        // ]
        commit('setFilteredEcheck', r.data.Data);
      }
    });
  },
};

export default {
  namespaced: true,
  state,
  getters,
  mutations,
  actions,
};
